import React, { useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { MTextBlockSimple, MTextBlockSimpleAlignment } from '../../molecules/MTextBlockSimple';
import { MButton, MButtonType, MButtonSize } from '../../molecules/MButton';
import { AIcon } from '../../atoms/AIcon';
import { ABody } from '../../atoms/ABody';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { AHeaderSize, AHeaderTag } from '../../atoms/AHeader/AHeader.types';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { MSlot } from '../../molecules/MSlot';
import * as Styled from './OTabContainer.styles';
import type { OTabContainerProps, OTabContainerAlignment } from './OTabContainer.types';

const TEST_ID = 'mi-web-ui-kit-OTabContainer';

export const OTabContainer: React.FC<OTabContainerProps> = ({
  alignment = 'Left' as OTabContainerAlignment,
  mContentHeader,
  mTabGroup,
  pagePath,
  itemPath,
  isAuthorMode = false,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const handleTabClick = (index: number) => {
    setActiveTabIndex(index);
  };

  const renderContentHeader = () => {
    if (!mContentHeader) return null;

    return (
      <Styled.ContentHeaderWrapper alignment={alignment}>
        {mContentHeader.mTextBlockSimple && (
          <MTextBlockSimple
            eyebrowText={mContentHeader.mTextBlockSimple.aEyebrow?.eyebrowText}
            headerText={mContentHeader.mTextBlockSimple.aHeader?.text}
            headerSize={mContentHeader.mTextBlockSimple.aHeader?.size as AHeaderSize}
            headerTag={mContentHeader.mTextBlockSimple.aHeader?.headerTag as AHeaderTag}
            bodyText={mContentHeader.mTextBlockSimple.aBody?.bodyText}
            bodySize={mContentHeader.mTextBlockSimple.aBody?.size as ABodySize}
            bodyColor={mContentHeader.mTextBlockSimple.aBody?.color as ABodyColor}
            alignment={mContentHeader.alignment as MTextBlockSimpleAlignment}
          />
        )}
        {(mContentHeader.cta || mContentHeader.secondaryCta) && (
          <Styled.ButtonGroup alignment={alignment}>
            {mContentHeader.cta && mContentHeader.mButton && (
              <MButton
                buttonText={mContentHeader.mButton.buttonText}
                variant={mContentHeader.mButton.type as MButtonType}
                size={mContentHeader.mButton.size as MButtonSize}
                ctaLink={mContentHeader.mButton.ctaLink}
                openInANewTab={mContentHeader.mButton.openInANewTab === 'true'}
              />
            )}
            {mContentHeader.secondaryCta && mContentHeader.mButton2 && (
              <MButton
                buttonText={mContentHeader.mButton2.buttonText}
                variant={mContentHeader.mButton2.type as MButtonType}
                size={mContentHeader.mButton2.size as MButtonSize}
                ctaLink={mContentHeader.mButton2.ctaLink}
                openInANewTab={mContentHeader.mButton2.openInANewTab === 'true'}
              />
            )}
          </Styled.ButtonGroup>
        )}
      </Styled.ContentHeaderWrapper>
    );
  };

  const renderTabControls = () => {
    if (!mTabGroup?.mTabItemMulti || mTabGroup.mTabItemMulti.length === 0) return null;

    const showIcon = mTabGroup.icon === 'true';
    const showDescription = mTabGroup.description === 'true';

    return (
      <Styled.TabControlsWrapper alignment={(mTabGroup.alignment as OTabContainerAlignment) || 'Left'}>
        <Styled.TabControlsList alignment={(mTabGroup.alignment as OTabContainerAlignment) || 'Left'}>
          {mTabGroup.mTabItemMulti.map((tabItem, index) => (
            <Styled.TabControlItem
              key={tabItem.nodeName || index}
              isActive={activeTabIndex === index}
              onClick={() => handleTabClick(index)}
            >
              {showIcon && tabItem.mTabControl?.aIcon && (
                <AIcon icon={tabItem.mTabControl.aIcon.icon || ''} size={tabItem.mTabControl.aIcon.size as AIconSize} />
              )}
              <Styled.TabLabelWrapper>
                {tabItem.mTabControl?.aLabel && (
                  <ABody
                    bodyText={tabItem.mTabControl.aLabel.text || ''}
                    size={(tabItem.mTabControl.aLabel.size as ABodySize) || ABodySize.M}
                  />
                )}
                {showDescription && tabItem.mTabControl?.aLabel2 && (
                  <ABody bodyText={tabItem.mTabControl.aLabel2.text || ''} size={ABodySize.S} />
                )}
              </Styled.TabLabelWrapper>
            </Styled.TabControlItem>
          ))}
        </Styled.TabControlsList>
      </Styled.TabControlsWrapper>
    );
  };

  const renderTabContent = () => {
    if (!mTabGroup?.mTabItemMulti || mTabGroup.mTabItemMulti.length === 0) return null;

    const activeTab = mTabGroup.mTabItemMulti[activeTabIndex];
    if (!activeTab?.mSlot) return null;

    return (
      <Styled.TabContentWrapper>
        <MSlot
          pagePath={pagePath || ''}
          itemPath={`${itemPath || ''}/mTabGroup/mTabItemMulti/${activeTab.nodeName || ''}`}
          isAuthorMode={isAuthorMode}
          cqItems={activeTab.mSlot[':items'] || {}}
          model={activeTab.mSlot}
        />
      </Styled.TabContentWrapper>
    );
  };

  return (
    <Styled.Wrapper data-component-name={TEST_ID} data-testid={TEST_ID}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            {renderContentHeader()}
            {renderTabControls()}
            {renderTabContent()}
          </div>
        </div>
      </div>
    </Styled.Wrapper>
  );
};

export const OTabContainerEditable = (props: OTabContainerProps) => {
  return (
    <EditableComponent
      config={{
        emptyLabel: 'Tab Container',
        isEmpty: () => false,
        resourceType: 'mi-aem-utils/components/content/o-tab-container',
      }}
      {...props}
    >
      <OTabContainer {...props} />
    </EditableComponent>
  );
};
